import React, {useMemo} from 'react';
import Form from 'react-bootstrap/Form';
import {CSSObject} from '@emotion/react';
import Select from 'react-select';
import useData from '@/utils/useData';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {ChartQuery} from 'entities/chart';
import {FormikT, FormikTValues} from 'utils/types';
import {aggregationChoicesFiltersCross, EventFilter, EventFilterGroup} from '@/views/storyboard/utils/chartConstants';
import {OptionProps} from 'react-select/dist/declarations/src/components/Option';
import {SingleValue} from 'react-select/dist/declarations/src/types';
import {Tooltip} from '@/components/tooltips';
import i18n from 'i18n';
import Icon from '@/components/icons/Icon';
import {resolveRestrictedFields} from '@/utils/fieldRegistry';


interface Option {
    value: string;
    label: string;
    type?: string;
    isDisabled?: boolean;
}

interface Props {
    values: FormikTValues<ChartQuery>;
    setFieldValue: FormikT<ChartQuery>['setFieldValue'];
    eventFilters?: EventFilter[];
    eventFiltersLoading?: boolean;
}

export const ChartAggregationsSelectCross = React.memo<Props>((
    {
        values,
        setFieldValue,
        eventFilters: eventFiltersProp,
        eventFiltersLoading,
    },
) => {
    const [eventFilterGroupList, loadingFromData] = useData<EventFilterGroup>(window.URLS.eventFiltersResource, {
        storeResolver: 'eventFilters',
        actionResolver: 'loadEventFilters',
        useCachedData: true,
    });

    const {userData} = useOptimizedConfig();
    const restrictedFields: string[] = useMemo(() => resolveRestrictedFields(userData?.restrictedFieldKeys), [userData?.restrictedFieldKeys]);

    const isRestricted = (filterValue: string) => {
        if (!restrictedFields.length) return false;
        const fieldName = filterValue.replace('all_fields.', '').replace('.keyword', '');
        return restrictedFields.includes(fieldName);
    };

    const eventFilters: EventFilter[] = eventFiltersProp ?? [
        ...(eventFilterGroupList.core || []),
        ...(eventFilterGroupList.prioritised || []),
        ...(eventFilterGroupList.non_prioritised || []),
        ...(eventFilterGroupList.others || []),
    ];
    const loading = eventFiltersProp !== undefined ? (eventFiltersLoading ?? false) : loadingFromData;

    const metricAggregationOptions = [
        {
            value: '',
            label: i18n.t('chart_settings_metric_none_count_only.text'),
        },
        ...aggregationChoicesFiltersCross[values.output ?? 'default'](eventFilters)
            .filter((el) => el[4] === true)
            .map((el) => ({
                value: el[0],
                label: el[1],
                type: el[2],
                isDisabled: isRestricted(el[0]),
            })),
    ];
    const selectedMetricAggregationOption = metricAggregationOptions.find(
        (option) => option.value === (values.metric_aggregations_aggs_item || ''),
    ) || metricAggregationOptions[0];

    return (
        <Form.Group className="cs-field" controlId="aggsItem" >
            <label className="cs-field__label">
                {i18n.t('jt_metric_aggregate.text')}
                <Tooltip
                    content={i18n.t('jt_select_metric.text')}
                    placement="top"
                >
                    <button
                        type="button"
                        className="btn btn-link p-0 ml-2"
                        style={{cursor: 'pointer', border: 'none', background: 'none'}}
                        data-cy="chart-aggregations-select-cross-btn">
                        <Icon name="mdi-information-outline" className='text-info' size='xs' />
                    </button>
                </Tooltip>
            </label>
            <Select<Option>
                id="chart-settings-form-aggregate-on"
                value={selectedMetricAggregationOption}
                name="aggs_item"
                onChange={(value: SingleValue<Option>) => {
                    if (value?.isDisabled) return;
                    setFieldValue('metric_aggregations_aggs_item', value?.value || null);
                }}
                isLoading={loading}
                isClearable={false}
                loadingMessage={() => i18n.t('jt_loading_opt.text')}
                maxMenuHeight={180}
                menuPlacement="auto"
                menuShouldScrollIntoView={true}
                isOptionDisabled={(option) => !!option.isDisabled}
                options={metricAggregationOptions}
                formatOptionLabel={(option) => (
                    <span className="d-inline-flex align-items-center">
                        {option.label}
                        {option.isDisabled && <Icon name="mdi-lock" size="0.85em" className="ml-1" />}
                    </span>
                )}
                styles={{
                    control: (base: CSSObject): CSSObject => ({...base, minHeight: 34, height: 34}),
                    valueContainer: (base: CSSObject): CSSObject => ({...base, padding: '0 8px'}),
                    indicatorsContainer: (base: CSSObject): CSSObject => ({...base, height: 34}),
                    menu: (base: CSSObject): CSSObject => ({...base, marginBottom: 8}),
                    menuList: (base: CSSObject): CSSObject => ({...base, maxHeight: 180}),
                    option: (base: CSSObject, state: OptionProps<Option, false>): CSSObject => {
                        let additionalStyles: CSSObject;
                        if (state.data?.isDisabled) {
                            additionalStyles = {
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                color: '#999',
                                cursor: 'not-allowed',
                            };
                        } else if (!state.isFocused) {
                            additionalStyles = {
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            };
                        } else {
                            additionalStyles = {
                                wordBreak: 'break-word',
                            };
                        }
                        return {
                            ...base,
                            background: !state.data?.isDisabled && state.isFocused
                                ? 'rgba(0, 158, 251, 0.08)'
                                : 'transparent',
                            backgroundColor: !state.data?.isDisabled && state.isFocused
                                ? 'rgba(0, 158, 251, 0.08)'
                                : 'transparent',
                            color: 'inherit',
                            ':active': {
                                ...base[':active'],
                                backgroundColor: !state.data?.isDisabled
                                    ? 'rgba(0, 158, 251, 0.08)'
                                    : 'transparent',
                            },
                            ...additionalStyles,
                        };
                    },
                }}
            />
        </Form.Group >
    );
});
